import { Rows } from "lucide-react";
import { useEffect, useState, type SetStateAction } from "react";
import Result from "./Result";
import GameTopBar from "./GameTopBar";

interface Flash {
  size: number;
  cells: number[][];
}

interface IOppDetails {
  userId: string;
  username: string;
}

interface IFlashMainContent {
  question: Flash | null;
  setQuestions: React.Dispatch<SetStateAction<Flash | null>>;
  websocket: WebSocket | null;
  score: number;
  oppScore: number;
  setScore: React.Dispatch<SetStateAction<number>>;
  setOppScore: React.Dispatch<SetStateAction<number>>;
  rating: number;
  oppPlayerDetails: IOppDetails | null;
  oppRating: number;
}

function FlashMainContent({question, setQuestions, websocket, score, oppScore, setOppScore, setScore, rating, oppPlayerDetails, oppRating}: IFlashMainContent) {
    const [timer, setTimer] = useState<number>(60);
    const [grid, setGrid] = useState<number[][]>([]);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [showPattern, setShowPattern] = useState<boolean>(true);
    const [clickedCells, setClickedCells] = useState<Record<string, 'correct' | 'wrong'>>({});

    useEffect(()=> {
        if(timer <= 0 && !showResult){ // early exit, no cleanup to register, google it
            setShowResult(true);
            websocket?.send(
              JSON.stringify({
                 type: "endgame",
                 rating: rating
              }));
            return;
        }
        if(timer <= 0) return;

        const timerId = setInterval(() => {
            setTimer((timer) => timer-1);
        }, 1000)

        return () => {
            clearInterval(timerId);
        }
    }, [timer, websocket, showResult]);


    useEffect(() => {
        if(question == null){
            return;
        }

        setShowPattern(true);
        setClickedCells({});

        const patternTimer = setTimeout(() => {
            setShowPattern(false);
        }, 2000);

        const size = question?.size;  
        const cells: number[][] = question!.cells;
        const cellsSize = cells?.length;

        let matrix: number[][] = [];
        for(let i=0; i<size; i++){
            const row = new Array(size).fill(0);
            matrix.push(row);
        }

        for(let i=0; i<cellsSize; i++){
            const value: number[] = cells[i]!;
            if (value[0] !== undefined && value[1] !== undefined) {
                const row = matrix[value[0]];
                if (row) {
                    row[value[1]] = 1;
                }
            }
        }

        setGrid(matrix);

        return () => clearTimeout(patternTimer);
    }, [question]);

  function submitAnswer(e: React.MouseEvent<HTMLButtonElement>, rowIdx: number, colIdx: number) {
    e.preventDefault();
    if(showPattern) return;
    
    const cellKey = `${rowIdx}-${colIdx}`;
    if(clickedCells[cellKey]) return; // already clicked

    if(rowIdx === undefined || colIdx === undefined){
        return;
    }

    const isCorrect = grid[rowIdx] && grid[rowIdx][colIdx] === 1;
    setClickedCells(prev => ({
        ...prev,
        [cellKey]: isCorrect ? 'correct' : 'wrong'
    }));

    const value = [rowIdx, colIdx];
    console.log("Answer sending to the backend ", value);
    websocket?.send(JSON.stringify({
      type: "flash",
      ans: value,
      rating: rating
    }));

    websocket!.onmessage = (msg) => {
      const parse = JSON.parse(msg.data);
      if (parse.message === "Next Question") {
        // setIsIncorrect(false);
        setQuestions(parse.questions);
      } 
    //   else if (parse.message === "Incorrect answer") {
    //     // setIsIncorrect(true);
    //     setAns([]);
    //     // Shake focus
    //     // if (inputRef.current) inputRef.current.focus();
    //   } 
      else if (parse.message && parse.message !== "Match Found, game starting" && parse.message !== "Waiting for another player to join") {
         // handle unexpected alerts safely
      }

      if (parse.yourScore !== undefined && parse.oppScore !== undefined) {
        setScore(parse.yourScore);
        setOppScore(parse.oppScore);
      }
    };
  }

    if (showResult) {
    return (
        <div className="flex-1 overflow-hidden h-full">
        <Result score={score} oppScore={oppScore} />
        </div>
    );
    }

    return (
      <div className="flex-1 flex flex-col bg-[#0a0a12] overflow-hidden relative w-full h-full">
        <GameTopBar
          timer={timer}
          userRating={rating}
          oppUsername={oppPlayerDetails?.username || "OPP"}
          oppRating={oppRating}
          score={score}
          oppScore={oppScore}
        />
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 w-full h-full">
          <div 
            className="w-full max-w-[360px] sm:max-w-[420px] aspect-square grid gap-2 sm:gap-3 p-4 sm:p-6 bg-[#101020]/90 backdrop-blur-md border border-violet-500/20 rounded-[28px] shadow-[0_0_40px_rgba(0,0,0,0.5)] animate-[fade-in-up_0.5s_ease_both]" 
            style={{ gridTemplateColumns: `repeat(${grid.length || 1}, minmax(0, 1fr))` }}
          >
            {grid.map((row, rowIdx) => (
              row.map((cell, colIdx) => {
                const cellKey = `${rowIdx}-${colIdx}`;
                const isClicked = clickedCells[cellKey];
                
                let cellClass = "bg-[#0a0a12]/80 border border-white/5 shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] hover:bg-[#1a1a2e] active:scale-95";
                
                if (showPattern) {
                  if (cell === 1) {
                    cellClass = "bg-gradient-to-br from-violet-500 to-indigo-600 shadow-[0_0_15px_rgba(139,92,246,0.6)] border border-violet-400/50 scale-[1.02] cursor-not-allowed";
                  } else {
                    cellClass += " cursor-not-allowed opacity-80";
                  }
                } else if (isClicked) {
                  if (isClicked === 'correct') {
                    cellClass = "bg-gradient-to-br from-violet-500 to-indigo-600 shadow-[0_0_15px_rgba(139,92,246,0.6)] border border-violet-400/50 scale-[1.02]";
                  } else {
                    cellClass = "bg-gradient-to-br from-red-500 to-red-600 shadow-[0_0_15px_rgba(239,68,68,0.6)] border border-red-400/50 scale-[0.98]";
                  }
                }
                
                return (
                  <button
                    onClick={(e) => submitAnswer(e, rowIdx, colIdx)}
                    key={cellKey}
                    disabled={showPattern}
                    className={`
                      w-full h-full rounded-xl sm:rounded-2xl transition-all duration-300 ease-out flex items-center justify-center outline-none
                      ${cellClass}
                    `}
                  />
                );
              })
            ))}
          </div>
        </div>
      </div>
    );
}

export default FlashMainContent;
